import Button from '@rezdy/components/Button';
import AuthRoute, { IFormContainerProps } from './AuthRoute';
import Field from '@rezdy/components/Form/Field';
import React from 'react';
import TextInput from '@rezdy/components/Form/FormElements/TextInput';
import routes from '../constants/routes';
import routing from '../services/routing';
import Grid from '@rezdy/components/Grid';
import Message from './Message';
import { isRezdyEmail } from '../services/utilities';
import { Trans, withTranslation } from 'react-i18next';
import { withSSOProvider } from '../pages/ChangeEmail/WithSSOProvider';
import { CONTACT_SUPPORT } from '../constants/auth';
import { Link } from 'react-router-dom';

export interface ChangeEmailProps extends IFormContainerProps {
    isSSOUser: boolean;
}

export class View extends AuthRoute<ChangeEmailProps> {
    public state = {
        data: {
            newEmail: '',
            confirmNewEmail: '',
        },
        isDirty: false,
        isLoading: false,
        isSignedIn: false,
        message: '',
        isDisabled: false,
    };

    public constructor(props: Readonly<ChangeEmailProps>) {
        super(props);
        this.state.isDisabled = isRezdyEmail(this.props.data?.email);
    }

    public onSubmit = () => {
        const { action } = this.props;
        const { isLoading, data, isDisabled } = this.state;

        // Prevent submission if disabled.
        if (!isLoading && !isDisabled) {
            this.setState({ isLoading: true }, async () => {
                try {
                    await action(data);
                } catch (error) {
                    if (error instanceof Error) {
                        this.setState({
                            isDirty: true,
                            isLoading: false,
                            message: error.message as string,
                        });
                    } else {
                        this.setState({
                            isDirty: true,
                            isLoading: false,
                            message: 'Unknown',
                        });
                    }
                }
            });
        }
    };

    public onSecondaryClick = () => {
        routing.push(routes.PROFILE);
    };

    public onFieldChange = (formData: { id?: string; value: string; isValid?: boolean }) => {
        const { id, value } = formData;
        const { data } = this.state;

        if (id !== undefined) {
            this.setState({
                data: {
                    ...data,
                    [id]: value,
                },
            });
        }
    };
    public renderBody = () => {
        const { email } = this.props.data;
        const { isDisabled } = this.state;
        const { i18n, isSSOUser } = this.props;

        if (isSSOUser) {
            return (
                <>
                    <Field label={i18n.t('email')} name="currentEmail">
                        <TextInput defaultValue={email} disabled={true} />
                    </Field>
                    <Message as="p" isDirty={false}>
                        <Trans i18nKey="sso_change_email_p1" />
                        <br />
                        <br />
                        <Trans i18nKey="sso_change_email_p2">
                            If you require more assistance, please
                            <Link to={CONTACT_SUPPORT} target="_blank">
                                contact Rezdy Support
                            </Link>
                        </Trans>
                    </Message>
                    <Trans i18nKey="sso_change_email" />
                </>
            );
        }

        return (
            <>
                {isDisabled && (
                    <Message as="p" isDirty={true}>
                        {i18n.t('rezdy_account_email')}
                    </Message>
                )}
                <React.Fragment>
                    <Field label={i18n.t('email')} name="currentEmail">
                        <TextInput defaultValue={email} disabled={true} />
                    </Field>
                    <Field label={i18n.t('email', { context: 'new' })} name="newEmail">
                        <TextInput onChange={this.onFieldChange} type="email" disabled={isDisabled} />
                    </Field>
                    <Field label={i18n.t('email', { context: 'reEnter' })} name="confirmNewEmail">
                        <TextInput onChange={this.onFieldChange} type="email" disabled={isDisabled} />
                    </Field>
                </React.Fragment>
            </>
        );
    };

    public renderFormFooter = () => {
        const { isLoading, isDisabled } = this.state;
        const { context, i18n } = this.props;
        return (
            <Grid.Container cols="auto auto" colGap>
                <Grid.Cell>
                    <Button action="secondary" onClick={this.onSecondaryClick} reversed tight width="100%">
                        {i18n.t('secondaryAction', { context: 'cancel' })}
                    </Button>
                </Grid.Cell>
                <Grid.Cell>
                    {!isDisabled && (
                        <Button type="submit" action="primary" loading={isLoading} width="100%" tight>
                            {i18n.t('primaryAction', { context })}
                        </Button>
                    )}
                </Grid.Cell>
            </Grid.Container>
        );
    };
}

export default withTranslation()(withSSOProvider(View));
